import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarDays } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Age: 30 Years Old (Born 22 March 1996)";
const DESC =
  "Raj Shamani is 30 years old — born 22 March 1996 in Indore, Madhya Pradesh. Full date of birth, birthday, zodiac sign and what he built at each age.";
const URL = "https://rajshamani.life/raj-shamani-age";
const OG = OG_ABOUT;

const faq = [
  {
    q: "How old is Raj Shamani?",
    a: "Raj Shamani was born on 22 March 1996, which makes him 30 years old as of 2026. He turns a year older every 22 March.",
  },
  {
    q: "What is Raj Shamani's date of birth?",
    a: "Raj Shamani's date of birth is 22 March 1996. He was born in Indore, Madhya Pradesh, India, into a Marwari business family.",
  },
  {
    q: "When is Raj Shamani's birthday?",
    a: "Raj Shamani's birthday is 22 March. He was born on 22 March 1996 in Indore.",
  },
  {
    q: "What is Raj Shamani's zodiac sign?",
    a: "Born on 22 March, Raj Shamani's zodiac sign is Aries — the sign that begins on 21 March.",
  },
  {
    q: "At what age did Raj Shamani start business?",
    a: "Raj Shamani joined the family manufacturing business, Shamani Industries, at the age of 16 in 2012. He started on the factory floor learning procurement, manufacturing and B2B distribution before he turned 20.",
  },
  {
    q: "How old was Raj Shamani when he started the Figuring Out podcast?",
    a: "Raj launched the Figuring Out podcast in 2021, when he was 25 years old. It has since become one of India's most-watched long-form business shows.",
  },
  {
    q: "How old was Raj Shamani when he spoke at the United Nations?",
    a: "He spoke at the United Nations on youth entrepreneurship in 2017, at the age of 21.",
  },
];

const ageTimeline = [
  ["16", "2012", "Joined Shamani Industries and started working on the factory floor."],
  ["18", "2014", "Completed schooling at Daly College, Indore."],
  ["21", "2017", "Spoke at the United Nations on youth entrepreneurship."],
  ["22", "2018", "Began publishing daily business content on LinkedIn and Instagram."],
  ["25", "2021", "Launched the Figuring Out podcast."],
  ["26", "2022", "Published his book Build, Don't Talk and started angel investing."],
  ["27", "2023", "Co-founded House of X, a D2C venture studio."],
  ["30", "2026", "Director at Shamani Industries, podcaster, speaker and angel investor."],
];

export const Route = createFileRoute("/raj-shamani-age")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani age and date of birth", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: AgePage,
});

function AgePage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Age" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <CalendarDays size={14} /> Date of birth
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani <span className="text-gradient-gold">age</span>: born 22 March 1996
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani was born on <strong className="text-foreground">22 March 1996</strong> in
            Indore, Madhya Pradesh, which makes him{" "}
            <strong className="text-foreground">30 years old in 2026</strong>. His birthday falls on
            22 March and his zodiac sign is Aries. This page collects the verified date-of-birth
            details plus what he had actually built at each age.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Raj Shamani age at a glance</h2>
        <dl className="rounded-xl border border-border bg-card divide-y divide-border/60">
          {[
            ["Date of birth", "22 March 1996"],
            ["Age in 2026", "30 years"],
            ["Birthday", "22 March"],
            ["Zodiac sign", "Aries"],
            ["Birthplace", "Indore, Madhya Pradesh, India"],
            ["Nationality", "Indian"],
            ["Started working", "Age 16, at Shamani Industries"],
          ].map(([k, v]) => (
            <div key={k} className="px-6 py-4 flex flex-wrap gap-2 justify-between">
              <dt className="text-sm uppercase tracking-wide text-gold">{k}</dt>
              <dd className="text-muted-foreground">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">What he built at each age</h2>
        <p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
          The reason the question gets searched so often is that most of Raj Shamani&apos;s public
          milestones happened unusually early. Here is the timeline mapped to his age rather than
          to calendar years.
        </p>
        <div className="rounded-xl border border-border bg-card divide-y divide-border/60">
          {ageTimeline.map(([age, year, what]) => (
            <div key={year} className="px-6 py-5 grid sm:grid-cols-[80px_70px_1fr] gap-3">
              <div className="text-gold font-display font-bold text-xl">{age}</div>
              <div className="text-sm text-muted-foreground pt-1">{year}</div>
              <div className="text-sm text-muted-foreground leading-relaxed">{what}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-muted-foreground">
          For the full biography see the{" "}
          <Link to="/about" className="text-gold underline underline-offset-4">
            Raj Shamani wiki
          </Link>
          , or read about his{" "}
          <Link to="/raj-shamani-family" className="text-gold underline underline-offset-4">
            family and education
          </Link>{" "}
          and{" "}
          <Link to="/raj-shamani-net-worth" className="text-gold underline underline-offset-4">
            net worth
          </Link>
          .
        </p>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-age" />

      <RelatedPages pages={["aion", "about", "faq", "podcast", "book", "ventures"]} />
    </div>
  );
}
